import React, { useEffect, useMemo } from 'react'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { usePrefetchTasks, usePrefetchUsers, useTaskDisplayName, useUserDisplayName } from '../../modules/entityLookups'

export interface ActivityItem {
    id: string
    action: string
    userId?: string | null
    userEmail?: string | null
    service?: string | null
    details?: string | null
    timestamp: string
}

const getActivityDisplay = (action: string) => {
    if (action.includes('user_register')) return { icon: 'U', color: 'bg-blue-500' }
    if (action.includes('user_login')) return { icon: 'L', color: 'bg-indigo-500' }
    if (action.includes('challenge')) return { icon: 'C', color: 'bg-green-500' }
    if (action.includes('task')) return { icon: 'T', color: 'bg-purple-500' }
    if (action.includes('feedback')) return { icon: 'F', color: 'bg-orange-500' }
    return { icon: 'A', color: 'bg-gray-500' }
}

const parseDetails = (details?: string | null): Record<string, unknown> => {
    try {
        return JSON.parse(details ?? '{}')
    } catch {
        return {}
    }
}

const extractTaskId = (details?: string | null) => {
    const parsed = parseDetails(details) as { taskId?: unknown }
    return typeof parsed.taskId === 'string' ? parsed.taskId : null
}

const ActivityLogRow: React.FC<{ activity: ActivityItem }> = ({ activity }) => {
    const fallbackName = activity.userEmail ?? activity.userId ?? 'Unknown user'
    const { displayName, isLoading } = useUserDisplayName(activity.userId, { fallback: fallbackName })
    const display = getActivityDisplay(activity.action)
    const showUserDetails = Boolean(activity.userId || activity.userEmail)

    const detailsObject = useMemo(() => parseDetails(activity.details), [activity.details])
    const rawTaskId = (detailsObject as { taskId?: unknown }).taskId
    const taskId = typeof rawTaskId === 'string' ? rawTaskId : null
    const { displayName: taskName, isLoading: isTaskLoading } = useTaskDisplayName(taskId, {
        fallback: taskId ?? 'Unknown task'
    })

    return (
        <div className="flex gap-3 p-3 bg-background rounded-lg border border-border">
            <div className="flex-shrink-0">
                <span className={`h-8 w-8 rounded-full ${display.color} flex items-center justify-center`}>
                    <span className="text-white text-xs font-medium">{display.icon}</span>
                </span>
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <p className="text-sm text-muted-foreground">
                            {showUserDetails && (
                                <span className="font-medium text-foreground me-2">
                                    {isLoading ? 'Loading...' : displayName}
                                </span>
                            )}
                            <span className="font-medium text-foreground">- {activity.action.replace(/_/g, ' ')}</span>
                            {taskId && (
                                <span className="font-medium text-foreground">
                                    {' '}
                                    - {isTaskLoading ? 'Loading task...' : taskName}
                                </span>
                            )}
                        </p>
                        {!taskId && activity.details && (
                            <p className="mt-1 text-xs text-muted-foreground truncate">{activity.details}</p>
                        )}
                    </div>
                    <div className="flex-shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                        <RelativeDateTime isoDateString={activity.timestamp} />
                    </div>
                </div>
            </div>
        </div>
    )
}

export const ActivityLogList: React.FC<{ items: ActivityItem[] }> = ({ items }) => {
    if (!items || items.length === 0) {
        return null
    }

    const prefetchUsers = usePrefetchUsers()
    const prefetchTasks = usePrefetchTasks()
    const userIds = useMemo(() => items.map((activity) => activity.userId), [items])
    const taskIds = useMemo(() => items.map((activity) => extractTaskId(activity.details)), [items])

    useEffect(() => {
        void prefetchUsers(userIds)
    }, [prefetchUsers, userIds])

    useEffect(() => {
        void prefetchTasks(taskIds)
    }, [prefetchTasks, taskIds])

    return (
        <div className="space-y-4 max-h-[300px] overflow-y-auto custom-scrollbar">
            {items.map((activity) => (
                <ActivityLogRow key={activity.id} activity={activity} />
            ))}
        </div>
    )
}
